import type { FacebookPixelTrackerPayload } from 'lib/facebookPixel';
import { loadGTM } from 'lib/gtmScript';
import * as metrics from 'lib/metrics';
import { useAuthStore } from 'lib/stores/authStore';
import type { FundMethods } from 'stores/zustand/registrationStore';
import { getActiveTradeAttribution } from 'stores/zustand/tradeAttributionStore';
import analyticsProvider from 'utils/analytics/analyticsProvider';
import { GrowthEvent, growthEventId } from 'utils/growthEventId';
import { uuidv4 } from 'utils/uuid';

/**
 * GTM service module — a sink, not reactive state: script loading and
 * deposit/trade conversion dispatch, with no observable state read by any
 * component.
 */

let started = false;

export function loadGtm(): void {
    if (started) return;
    started = true;

    // GTM tags hook history.pushState/replaceState; a misconfigured tag
    // throws on every nuqs URL update, flooding the dev console. Skip
    // loading the script locally — staging/prod still load it.
    if (process.env.NODE_ENV !== 'development') {
        loadGTM();
    }
}

export interface TradeAnalyticsParams {
    orderId: string;
    value: number;
    // Whether the user had not traded before this order, snapshotted by the caller
    // before any optimistic update to the hasTraded cache.
    hasNotTraded: boolean;
    tradedSize: number;
    restingSize: number;
    orderPrice: number;
    tradeValueUsd?: number;
    contentIds?: string[];
    eventTicker?: string;
    seriesTicker?: string;
    marketTicker?: string;
    orderType?: string;
    orderAction?: string;
    side?: string;
    eventCategory?: string;
    eventSubcategories?: string[];
}

/**
 * Sends all deposit analytics events (DepositInitiated, FirstDepositInitiated, HighValueDepositInitiated).
 * Call this from any deposit success handler to ensure consistent analytics across all deposit methods.
 *
 * @param depositId - The backend deposit/transfer ID for deterministic event ID generation.
 *   When provided, event_id = SHA-256(depositId + eventName) to match svc-growth-events for dedup.
 *   Pass uuidv4() from the caller when no deposit ID is available (e.g., card deposit success
 *   callbacks that fire before the transfer record is created).
 * @param isFirstDeposit - Snapshot taken by the caller *before* any deposit-polling/reload that
 *   could make hasDepositedBefore appear true, to preserve accuracy.
 */
export function sendDepositAnalytics(
    amountInDollars: number,
    depositMethod: FundMethods,
    depositId: string,
    isFirstDeposit: boolean,
): void {
    const userId = useAuthStore.getState().userId ?? '';
    const trackerPayload: FacebookPixelTrackerPayload = {
        user_id: userId,
        page: window.location.pathname,
        value: amountInDollars,
        currency: 'USD',
        event_id: growthEventId(depositId, GrowthEvent.Deposit),
        deposit_method: depositMethod,
    };

    const depositMetricBase = { amount: amountInDollars, currency: 'USD', deposit_method: depositMethod };

    // Send DepositInitiated events
    analyticsProvider.sendEvent('DepositInitiated', trackerPayload);
    metrics.recordDepositInitiated(depositMetricBase);

    // Send FirstDepositInitiated events if this is the user's first deposit.
    if (isFirstDeposit) {
        trackerPayload.event_id = growthEventId(depositId, GrowthEvent.FirstDeposit);
        analyticsProvider.sendEvent('FirstDepositInitiated', trackerPayload);
        metrics.recordDepositInitiated({ ...depositMetricBase, first_deposit: true });
    }

    // Send HighValueDepositInitiated events if amount > $100
    // High-value deposit events are web-only with no server equivalent — keep random IDs
    if (amountInDollars > 100) {
        trackerPayload.event_id = uuidv4();
        analyticsProvider.sendEvent('HighValueDepositInitiated', trackerPayload);
        metrics.recordDepositInitiated({ ...depositMetricBase, high_value: true });
    }
}

/**
 * Sends all trade analytics events (SubmitAnyOrder, and FirstTrade events if applicable).
 * Call this from order success handlers to ensure consistent analytics.
 */
export function sendTradeAnalytics(params: TradeAnalyticsParams): void {
    const trackerPayload: FacebookPixelTrackerPayload = {
        event_id: growthEventId(params.orderId, GrowthEvent.Trade),
        content_ids: params.contentIds,
        value: params.value,
        // Dollar notional (count x price) for partner conversion value. value above is
        // taker_fill_cost in cents and is 0 for resting limit orders, so it can't be used.
        trade_value_usd: params.tradeValueUsd,
        order_id: params.orderId,
        traded_size: params.tradedSize,
        resting_size: params.restingSize,
        order_price: params.orderPrice,
        portfolio: window.location.pathname.includes('portfolio'),
    };

    const hasNotTraded = params.hasNotTraded;

    // Social attribution goes to internal metrics only — never onto the
    // partner-pixel trackerPayload.
    const attribution = getActiveTradeAttribution(params.eventTicker);

    // Send trade events for all orders
    analyticsProvider.sendEvent('SubmitAnyOrder', trackerPayload);
    metrics.recordSubmitOrder({
        order_id: params.orderId,
        order_price: params.orderPrice,
        traded_size: params.tradedSize,
        resting_size: params.restingSize,
        value: params.value,
        portfolio: window.location.pathname.includes('portfolio'),
        first_trade: hasNotTraded,
        event_ticker: params.eventTicker,
        series_ticker: params.seriesTicker,
        market_ticker: params.marketTicker,
        order_type: params.orderType,
        order_action: params.orderAction,
        side: params.side,
        event_category: params.eventCategory,
        event_subcategories: params.eventSubcategories,
        ...(attribution && {
            attribution_source: attribution.source,
            attribution_post_id: attribution.post_id,
            attribution_event_ticker: attribution.event_ticker,
            attribution_age_ms: Date.now() - attribution.captured_at,
        }),
    });

    // Send first trade events if this is the user's first trade
    if (hasNotTraded) {
        trackerPayload.event_id = growthEventId(params.orderId, GrowthEvent.FirstTrade);
        analyticsProvider.sendEvent('SubmitOrder', trackerPayload);
    }
}
